/**
 * /checkout/success — Transportation Plan Activated.
 *
 * Branded post-payment confirmation. Replaces the generic
 * payment-success message. Uses location.state.confirmed (passed by
 * CheckoutPlan.handleSuccess) when present, falls back to a clean
 * activation screen otherwise.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Check, Calendar, MapPin, Navigation as DestIcon, Clock, Car, UsersRound, ShieldCheck, Download, Gift, Share2 } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";

import { api } from "@/shared/lib/api";
import { toast } from "sonner";

// Referral copy — pulled up so ops can update without hunting through
// JSX. Phase C1 owner directive (2026-02-18): all monetary "$5 back"
// language removed; card now is a non-monetary invitation. Actual
// referral endpoint will ship later; the shape can grow to include
// codes/counters when it does.
const REFERRAL_COPY = {
  headline: "Invite a friend to Link",
  body: "Share the Link app so friends can book their own weekly rides in Kansas City.",
};

// Build a Google Calendar quick-add URL from a plan window (Web fallback
// for platforms without an .ics handler). Not currently rendered — we
// ship the .ics download button. Kept exported for reuse in Phase B.
function _buildGoogleCalendarUrl({ start, end, title, details, location }) {
  const fmt = (d) => d.replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${fmt(start)}/${fmt(end)}`,
    details,
    location,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function CheckoutSuccess() {
  const location = useLocation();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const activated = sp.get("activated") === "1" || !!location.state?.confirmed;
  // Phase C1 · The referral card no longer carries a $5-back incentive
  // (owner directive 2026-02-18), so the pulsing "reward" affordance
  // was removed. The gift badge stays as a static brand accent but
  // does NOT animate anymore. `useReducedMotion` retained for future
  // motion additions on this surface.
  const reduced = useReducedMotion();

  const confirmed = location.state?.confirmed || null;
  const [pollStatus, setPollStatus] = useState(sessionId ? "checking" : (activated ? "paid" : "checking"));
  const [msg, setMsg] = useState(activated ? "Your transportation is on the calendar." : "Finalizing your transportation plan…");

  // Legacy /checkout/success?session_id=... path — Stripe-hosted session
  // returns here. Keep the poller alive for backward compatibility.
  useEffect(() => {
    if (!sessionId || activated) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setPollStatus("paid");
          setMsg("Your transportation is on the calendar.");
        } else if (data.status === "expired") {
          setPollStatus("expired");
          setMsg("This checkout session expired. Please try again.");
        } else if (attempts < 12) {
          attempts += 1;
          setTimeout(poll, 1200);
        } else {
          setPollStatus("timeout");
          setMsg("Still waiting for payment confirmation. Please check your email.");
        }
      } catch (_e) {
        if (cancelled) return;
        if (attempts < 12) {
          attempts += 1;
          setTimeout(poll, 1500);
        } else {
          setPollStatus("error");
          setMsg("We couldn't verify the payment. Please contact support if charged.");
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, activated]);

  const isPaid = pollStatus === "paid";

  return (
    <div className="min-h-[100dvh] bg-[#F9F7F4] text-[#0A1128]" data-testid="checkout-success-page">
      <header
        className="sticky top-0 z-30 bg-[#F9F7F4]/95 backdrop-blur border-b border-black/[0.06]"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-center">
          <img src="/assets/link-full-logo.svg?v=13" alt="Link" className="h-5 w-auto" data-testid="success-logo" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6 pb-24">
        {/* HERO */}
        <div className="text-center space-y-3 pt-4">
          <div className="inline-flex w-14 h-14 rounded-full bg-[#16a34a]/12 items-center justify-center mx-auto" aria-hidden="true">
            <Check size={28} strokeWidth={2.2} className="text-[#16a34a]" data-testid="success-icon" />
          </div>
          <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[#ed6500]">
            {isPaid ? "Transportation Activated" : pollStatus === "checking" ? "Finalizing…" : "Status"}
          </div>
          <h1
            className="font-serif text-[28px] sm:text-[34px] leading-tight"
            data-testid="success-heading"
          >
            {isPaid ? "Transportation Plan Activated" : pollStatus === "expired" ? "Checkout expired" : pollStatus === "checking" ? "Finalizing your plan" : "Status"}
          </h1>
          <p className="text-[14px] text-[#515E7B] max-w-md mx-auto" data-testid="checkout-message">{msg}</p>
        </div>

        {/* PLAN RECAP (when state was passed by CheckoutPlan) */}
        {confirmed && <PlanRecap confirmed={confirmed} />}

        {/* STATUS STRIP */}
        {isPaid && (
          <section className="rounded-3xl bg-[#0A1128] text-[#F9F7F4] p-5 space-y-3" data-testid="success-status">
            <div className="text-[11px] tracking-[0.28em] uppercase font-semibold text-[#ed6500]">Status</div>
            <ul className="space-y-2 text-[14px]">
              <StatusItem text="Plan active" delay={0.10} />
              <StatusItem text="Transportation scheduled" delay={0.28} />
              <StatusItem text="No surge pricing" delay={0.46} />
            </ul>
          </section>
        )}

        {/* CTA + secondary actions (calendar + referral).
            Only rendered on paid state — before that we still show a
            single "Back to map" button below so the user is never
            stranded on a spinning "Finalizing" screen. */}
        {isPaid ? (
          <>
            <div className="space-y-3 pt-2">
              <Link
                to="/trips"
                className="block w-full h-14 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[12px] tracking-[0.28em] uppercase font-semibold flex items-center justify-center transition-colors"
                data-testid="success-cta-trips"
              >
                View my rides
              </Link>

              {confirmed && (
                <button
                  type="button"
                  onClick={() => downloadIcs(confirmed)}
                  className="w-full h-12 rounded-2xl bg-white border border-black/10 hover:border-[#ed6500] text-[#0A1128] text-[12px] tracking-[0.24em] uppercase font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                  data-testid="success-cta-calendar"
                >
                  <Download size={14} strokeWidth={2} /> Add to Calendar
                </button>
              )}
            </div>

            {/* Referral card — non-monetary invitation (Phase C1).
                Placeholder for the Phase D referral endpoint. Share
                button uses the Web Share API where available and falls
                back to copy-to-clipboard. Gift badge is intentionally
                static — no $5-back pulse. */}
            <section
              className="rounded-3xl bg-gradient-to-br from-[#fff3e6] to-[#ffece0] border border-[#ed6500]/20 p-5 flex items-start gap-4 mt-4"
              data-testid="success-referral-card"
            >
              <div
                className="w-11 h-11 rounded-full bg-[#ed6500] text-white flex items-center justify-center shrink-0"
                data-testid="success-referral-badge"
              >
                <Gift size={18} strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-[#0A1128]" data-testid="success-referral-headline">
                  {REFERRAL_COPY.headline}
                </div>
                <p className="text-[12px] text-[#0A1128]/70 mt-0.5 leading-relaxed">
                  {REFERRAL_COPY.body}
                </p>
                <button
                  type="button"
                  onClick={shareReferralLink}
                  className="mt-3 inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#ed6500] hover:text-[#b34500]"
                  data-testid="success-referral-share"
                >
                  <Share2 size={12} strokeWidth={2.2} /> Share invite
                </button>
              </div>
            </section>

            <div className="flex items-center justify-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#515E7B] pt-4">
              <ShieldCheck size={12} strokeWidth={1.8} /> Secured by Stripe
            </div>
          </>
        ) : (
          <div className="space-y-3 pt-2">
            <Link
              to="/"
              className="block w-full h-14 rounded-2xl border border-black/15 text-[#0A1128] text-[12px] tracking-[0.28em] uppercase font-semibold flex items-center justify-center hover:bg-black/[0.04] transition-colors"
              data-testid="success-cta-home"
            >
              Back to map
            </Link>
            <div className="flex items-center justify-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#515E7B]">
              <ShieldCheck size={12} strokeWidth={1.8} /> Secured by Stripe
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

// ---- .ics generator ------------------------------------------------
// Builds a minimal, standards-compliant iCalendar file describing the
// PICKUP window as an all-day (or timed) event. Return window is a
// separate VEVENT. No external library — we emit the exact 8-line
// header/footer + fields so it opens cleanly in Apple/Google/Outlook.
function downloadIcs(confirmed) {
  try {
    const { plan, dayStr, pickupLabel, returnLabel, isWeekly } = confirmed;
    const now = new Date();
    const stamp = now.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
    const uid = `link-${stamp}-${Math.random().toString(36).slice(2, 8)}@link.hermesmobility.com`;
    const parseHM = (label) => {
      // "12:00–12:15 AM" → returns { start:"120000", end:"121500", isPM:false }.
      const m = /(\d{1,2}):(\d{2})[^\d]+(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(label || "");
      if (!m) return null;
      let sh = parseInt(m[1], 10);
      const sm = parseInt(m[2], 10);
      let eh = parseInt(m[3], 10);
      const em = parseInt(m[4], 10);
      const pm = (m[5] || "").toUpperCase() === "PM";
      if (pm && sh < 12) sh += 12;
      if (pm && eh < 12) eh += 12;
      if (!pm && sh === 12) sh = 0;
      if (!pm && eh === 12) eh = 0;
      return { sh, sm, eh, em };
    };
    // Anchor date — tomorrow at 00:00 as a placeholder if the plan
    // doesn't include an explicit start_date. Real Phase B route
    // integration will replace this with the actual first_pickup_date.
    const anchor = new Date();
    anchor.setDate(anchor.getDate() + 1);
    anchor.setHours(0, 0, 0, 0);
    const toIcs = (year, month, day, h, m) => {
      const pad = (n) => String(n).padStart(2, "0");
      return `${year}${pad(month + 1)}${pad(day)}T${pad(h)}${pad(m)}00`;
    };
    const events = [];
    const p = parseHM(pickupLabel);
    if (p) {
      events.push({
        summary: `Link pickup — ${plan?.pickup || "Origin"}`,
        description: `Scheduled Link pickup${plan?.destination ? ` → ${plan.destination}` : ""}. ${dayStr || ""}${isWeekly ? " (recurring weekly)" : ""}.`.trim(),
        location: plan?.pickup || "",
        start: toIcs(anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), p.sh, p.sm),
        end:   toIcs(anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), p.eh, p.em),
      });
    }
    const r = parseHM(returnLabel);
    if (r) {
      events.push({
        summary: `Link return — ${plan?.destination || "Destination"}`,
        description: `Scheduled Link return${plan?.pickup ? ` → ${plan.pickup}` : ""}. ${dayStr || ""}${isWeekly ? " (recurring weekly)" : ""}.`.trim(),
        location: plan?.destination || "",
        start: toIcs(anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), r.sh, r.sm),
        end:   toIcs(anchor.getFullYear(), anchor.getMonth(), anchor.getDate(), r.eh, r.em),
      });
    }
    if (events.length === 0) {
      toast.error("Ride times weren't captured — check your rides tab for full details.");
      return;
    }
    const lines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//Link//Ride Schedule//EN",
      "CALSCALE:GREGORIAN",
    ];
    for (const ev of events) {
      lines.push(
        "BEGIN:VEVENT",
        `UID:${uid}-${ev.summary.slice(0, 5).replace(/\W/g, "")}`,
        `DTSTAMP:${stamp}`,
        `DTSTART:${ev.start}`,
        `DTEND:${ev.end}`,
        `SUMMARY:${ev.summary.replace(/[\n,;\\]/g, " ")}`,
        `LOCATION:${(ev.location || "").replace(/[\n,;\\]/g, " ")}`,
        `DESCRIPTION:${(ev.description || "").replace(/[\n,;\\]/g, " ")}`,
        "END:VEVENT",
      );
    }
    lines.push("END:VCALENDAR");
    const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "link-ride.ics";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast.success("Calendar file downloaded");
  } catch (e) {
    toast.error("Couldn't build calendar file");
  }
}

// ---- Referral share ------------------------------------------------
async function shareReferralLink() {
  const shareUrl = `${window.location.origin}/welcome?ref=` +
    encodeURIComponent(("link-" + Math.random().toString(36).slice(2, 8)));
  const shareData = {
    title: "Ride with Link",
    text: "I use Link for scheduled rides — same price every day. Sign up and we both get $5.",
    url: shareUrl,
  };
  try {
    if (navigator.share) {
      await navigator.share(shareData);
      return;
    }
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Invite link copied");
  } catch (_e) {
    // User cancelled share sheet — silent.
  }
}

function PlanRecap({ confirmed }) {
  const { plan, dayStr, pickupLabel, returnLabel, seatsLabel, prefLabel, finalAmount, isWeekly } = confirmed;
  return (
    <section className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-5 space-y-4" data-testid="success-plan-recap">
      <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#0A1128]/55">Your transportation plan</div>

      <div className="space-y-3">
        <Row
          icon={<MapPin size={14} className="text-[#0074d4]" />}
          label="Pickup"
          value={plan.pickup}
          testId="success-pickup"
        />
        <Row
          icon={<DestIcon size={14} className="text-[#ed6500]" />}
          label="Destination"
          value={plan.destination}
          testId="success-destination"
        />
      </div>

      <div className="border-t border-black/[0.06] pt-4 space-y-3">
        <Row icon={<Calendar size={14} className="text-[#515E7B]" />} label="Days" value={dayStr || "—"} testId="success-days" />
        <div className="grid grid-cols-2 gap-4">
          <Row icon={<Clock size={14} className="text-[#515E7B]" />} label="Pickup window" value={pickupLabel} testId="success-pickup-window" inline />
          <Row icon={<Clock size={14} className="text-[#515E7B]" />} label="Return window" value={returnLabel} testId="success-return-window" inline />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Row
            icon={plan?.pref === "private" ? <Car size={14} className="text-[#ed6500]" /> : <UsersRound size={14} className="text-[#0074d4]" />}
            label="Transportation preference"
            value={prefLabel}
            testId="success-pref"
            inline
          />
          <Row icon={null} label="Seats reserved" value={seatsLabel} testId="success-seats" inline />
        </div>
      </div>

      <div className="border-t border-black/[0.06] pt-4 flex items-baseline justify-between">
        <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0A1128]">
          {isWeekly ? "Weekly transportation cost" : "Total"}
        </div>
        <div className="font-serif text-[26px] text-[#ed6500] tabular-nums" data-testid="success-total">
          ${Number(finalAmount || 0).toFixed(2)}
          {isWeekly && <span className="text-[11px] text-[#515E7B] font-normal font-sans ml-1">/ week</span>}
        </div>
      </div>
    </section>
  );
}

function Row({ icon, label, value, testId, inline }) {
  if (inline) {
    return (
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#515E7B]">{label}</div>
        <div className="text-[14px] text-[#0A1128] mt-0.5 flex items-center gap-1.5" data-testid={testId}>
          {icon}<span>{value || "—"}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3">
      <div className="w-4 flex justify-center pt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#515E7B]">{label}</div>
        <div className="text-[14px] text-[#0A1128] mt-0.5 break-words" data-testid={testId}>{value || "—"}</div>
      </div>
    </div>
  );
}

function StatusItem({ text, delay = 0 }) {
  // Staggered checkmark reveal (B3 payment-success sequence).
  // Under prefers-reduced-motion we short-circuit BOTH the row's
  // opacity/y transition AND the check icon's spring — items render
  // instantly in their final state with no stagger. Framer's JS
  // transitions are NOT affected by CSS `transition-duration:0`
  // (that only kills CSS transitions), so we gate explicitly via
  // useReducedMotion — the earlier comment was wrong and shipping
  // stagger to a reduced-motion user is a real accessibility bug.
  const reduced = useReducedMotion();
  const rowTransition = reduced
    ? { duration: 0, delay: 0 }
    : { duration: 0.28, ease: [0.22, 1, 0.36, 1], delay };
  const iconTransition = reduced
    ? { duration: 0, delay: 0 }
    : { duration: 0.36, ease: [0.34, 1.56, 0.64, 1], delay: delay + 0.04 };
  return (
    <motion.li
      className="flex items-center gap-2.5"
      initial={reduced ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={rowTransition}
      data-testid="success-status-item"
    >
      <motion.span
        initial={reduced ? { scale: 1, opacity: 1 } : { scale: 0.4, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={iconTransition}
        className="inline-flex"
        aria-hidden="true"
      >
        <Check size={14} strokeWidth={2.2} className="text-[#ed6500] shrink-0" />
      </motion.span>
      <span>{text}</span>
    </motion.li>
  );
}
